'use client';

import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { Phone, MessageCircle } from 'lucide-react';
import axios from 'axios';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trackConsumerEvent } from '@/libs/analytics/consumerEvents';
import { useAuthStore } from '@/presentation/hooks/useStores';

// Support categories aligned with PSS business model
const CALLBACK_TOPICS = [
  { value: 'payment', label: 'Payment Issues' },
  { value: 'order', label: 'Order & Delivery' },
  { value: 'account', label: 'Account & Verification' },
  { value: 'repscore', label: 'RepScore & Early Pickup' },
  { value: 'pss', label: 'Pay Small Small & Pay4Me' },
  { value: 'product', label: 'Product Inquiry' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'aftersale', label: 'After-Sale Support' },
  { value: 'other', label: 'Other' },
];

// Map frontend categories to backend reason enum
const CATEGORY_TO_REASON: Record<string, string> = {
  payment: 'PAYMENT_ISSUE',
  order: 'DELIVERY_ISSUE',
  account: 'GENERAL',
  repscore: 'GENERAL',
  pss: 'GENERAL',
  product: 'PRODUCT_INQUIRY',
  technical: 'GENERAL',
  aftersale: 'AFTER_SALES',
  other: 'OTHER',
};

// Route via the Next.js proxy so environments (staging/prod) stay consistent and
// partner headers/cookies behave the same as the rest of the app.
const PROXY_BASE_URL = (process.env.NEXT_PUBLIC_API_PROXY_BASE_URL || '/api/proxy').replace(/\/$/, '');
const CALLBACK_API_URL = `${PROXY_BASE_URL}/v1/support/callbacks/`;

interface RequestCallbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function RequestCallbackModal({ open, onOpenChange }: RequestCallbackModalProps) {
  const authStore = useAuthStore();
  const pathname = usePathname();
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user's phone number from auth store
  const userPhone = authStore.userPhoneNumber || '';
  const productIdFromPathname = pathname?.match(/^\/products\/([^/?#]+)/)?.[1] ?? '';
  const maskedPhone = userPhone
    ? `${userPhone.slice(0, 6)}***${userPhone.slice(-3)}`
    : '';

  // Reset state when modal closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedTopic('');
      setMessage('');
      setShowSuccess(false);
      setError(null);
    }
    onOpenChange(isOpen);
  };

  // Handle callback request submission
  const handleSubmit = async () => {
    if (!selectedTopic || !userPhone) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Map frontend category to backend reason
      const reason = CATEGORY_TO_REASON[selectedTopic] || 'GENERAL';
      const categoryLabel = CALLBACK_TOPICS.find((t) => t.value === selectedTopic)?.label;

      // Add category context to notes if mapping to GENERAL
      const notesWithContext =
        reason === 'GENERAL' && categoryLabel
          ? `[${categoryLabel}] ${message}`.trim()
          : message || undefined;

      const payload = {
        phone_number: userPhone,
        reason,
        notes: notesWithContext,
        preferred_channel: 'PHONE',
        preferred_window: 'ASAP',
      };

      // Get auth token if available
      const token =
        typeof window !== 'undefined'
          ? window.localStorage.getItem('motito_auth_token')
          : null;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      await axios.post(CALLBACK_API_URL, payload, { headers, timeout: 15000 });
      trackConsumerEvent({
        eventType: 'callback_requested',
        properties: {
          product_id: productIdFromPathname,
          topic: selectedTopic,
          preferred_channel: payload.preferred_channel,
          preferred_window: payload.preferred_window,
          surface: 'callback_modal',
          source_data: 'request_callback_modal',
        },
      });

      setShowSuccess(true);

      // Close modal after showing success
      setTimeout(() => {
        handleOpenChange(false);
      }, 2000);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.code === 'ECONNABORTED') {
          setError('Request timed out. Please check your connection and try again.');
        } else if (err.response?.status === 429) {
          setError('Too many requests. Please wait a moment and try again.');
        } else if (err.response?.status === 400) {
          setError('Invalid request. Please check your information and try again.');
        } else if (err.response?.status === 401) {
          setError('Your session has expired. Please sign in again.');
        } else if (!err.response) {
          setError('Unable to connect. Please check your internet connection.');
        } else {
          setError('Something went wrong. Please try again later.');
        }
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Chatwoot for Betty chat
  const handleChatWithBetty = () => {
    if (window.$chatwoot) {
      trackConsumerEvent({
        eventType: 'support_chat_opened',
        properties: {
          product_id: productIdFromPathname,
          channel: 'in_app_chat',
          surface: 'callback_modal',
          source_data: 'request_callback_modal',
        },
      });
      window.$chatwoot.toggle();
    }
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request a Callback</DialogTitle>
        </DialogHeader>

        {showSuccess ? (
          // Success state
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-100">
              <Phone className="size-8 text-green-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Callback Requested!
            </h3>
            <p className="text-sm text-gray-600">
              Our team will call you back shortly.
            </p>
          </div>
        ) : (
          // Main form
          <div className="space-y-4">
            {/* Topic Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                What&apos;s this about?
              </label>
              <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
                <SelectContent>
                  {CALLBACK_TOPICS.map((topic) => (
                    <SelectItem key={topic.value} value={topic.value}>
                      {topic.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional Message */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Tell us more (optional)
              </label>
              <Input
                placeholder="Briefly describe your issue..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            {/* Phone Confirmation or Login Prompt (only show login prompt after topic selected) */}
            {userPhone ? (
              <p className="text-sm text-gray-600">
                We&apos;ll call you at{' '}
                <span className="font-medium text-gray-900">{maskedPhone}</span>
              </p>
            ) : selectedTopic ? (
              <p className="text-sm text-amber-600">
                Please sign in to request a callback. We need your phone number to call you back.
              </p>
            ) : null}

            {/* Error Message */}
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={!selectedTopic || !userPhone || isSubmitting}
              className="w-full bg-motitoBlue"
            >
              {isSubmitting ? 'Requesting...' : 'Request Callback'}
            </Button>

            {/* Betty Nudge */}
            <div className="border-t pt-4">
              <button
                onClick={handleChatWithBetty}
                className="flex w-full items-center justify-center gap-2 text-sm text-gray-500 transition-colors hover:text-motitoBlue"
              >
                <MessageCircle className="size-4" />
                <span>Need faster help? Chat with Betty instead</span>
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RequestCallbackModal;
